// Shared client-side handling for the proactive TaxJar validation gate (NUB-315).
// The backend throws a GraphQLError with extensions.code === "TAX_NOT_CONFIRMED" from the
// payment / invoice-creation paths. Both Ops and Tech surfaces parse it the same way.

export enum TaxGateOutcome {
  CORRECTED = "CORRECTED",
  NOT_CONFIRMED = "NOT_CONFIRMED",
  TAXJAR_FAILED = "TAXJAR_FAILED",
}

export interface TaxGateInfo {
  outcome: TaxGateOutcome;
  reasons: string[];
  amountChanged: boolean;
  amountDue?: number;
}

// Pulls the tax-gate details out of an ApolloError (or anything with graphQLErrors).
export const parseTaxGateError = (error: any): TaxGateInfo | null => {
  const gqlErrors = error?.graphQLErrors ?? error?.networkError?.result?.errors ?? [];
  for (const e of gqlErrors) {
    const ext = e?.extensions;
    if (ext?.code === "TAX_NOT_CONFIRMED") {
      return {
        outcome: ext.outcome as TaxGateOutcome,
        reasons: ext.reasons ?? [],
        amountChanged: !!ext.amountChanged,
        amountDue: ext.amountDue,
      };
    }
  }
  return null;
};

// User-facing message per outcome. For CORRECTED we intentionally don't print the amount —
// the caller refetches the invoice and the UI shows the authoritative new total.
export const taxGateMessage = (info: TaxGateInfo, context: "payment" | "invoice" = "payment"): string => {
  const blockedAction = context === "invoice" ? "creating this invoice" : "collecting payment";
  switch (info.outcome) {
    case TaxGateOutcome.CORRECTED:
      return "Tax was updated for this invoice and the total changed. Please review and confirm the new amount before collecting payment.";
    case TaxGateOutcome.TAXJAR_FAILED:
      return context === "invoice"
        ? "Tax service is unavailable. This invoice can't be created right now — please try again shortly."
        : "Tax service is unavailable. Payment cannot proceed right now — please try again shortly.";
    case TaxGateOutcome.NOT_CONFIRMED:
    default:
      if (context === "invoice") {
        const issueCode = info.reasons?.length ? info.reasons.join(", ") : "UNKNOWN";
        return `Tax couldn't be confirmed for this invoice. Escalate to Application Bugs with issue code ${issueCode} before creating the invoice.`;
      }
      return `Tax could not be confirmed for this invoice${
        info.reasons?.length ? ` (${info.reasons.join(", ")})` : ""
      }. Resolve the flagged issues before ${blockedAction}.`;
  }
};
